import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  Target, Dumbbell, FileText, CheckCircle, Bot, User, Send, Loader2, Lock,
  ChevronLeft, ChevronRight, MessageSquare, ArrowRight, Lightbulb, TrendingUp, Sparkles, Star,
  BookOpen, MessageCircle, Wrench, Quote
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ModulePage() {
  const { week: weekParam } = useParams();
  const week = parseInt(weekParam);
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [openingQuestion, setOpeningQuestion] = useState(null);
  const [loadingOpener, setLoadingOpener] = useState(false);
  const [coachingStatus, setCoachingStatus] = useState({ user_messages: 0, coaching_complete: false });
  const [reflection, setReflection] = useState('');
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = `module-${user?.id}-week-${week}`;

  const isLocked = week > (user?.current_week || 1) && !user?.completed_modules?.includes(week);
  const isComplete = user?.completed_modules?.includes(week);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [modRes, histRes, statusRes] = await Promise.all([
          axios.get(`${API}/curriculum/${week}`, { withCredentials: true }),
          axios.get(`${API}/coach/history?session_id=${sessionId}`, { withCredentials: true }),
          axios.get(`${API}/progress/coaching-status/${week}`, { withCredentials: true }),
        ]);
        setModule(modRes.data);
        setMessages(histRes.data.messages || []);
        setCoachingStatus(statusRes.data);
        setCompleted(isComplete);

        // Fetch weekly summary if module is complete
        if (isComplete) {
          fetchSummary();
        }

        // Fetch opening question if no messages yet
        if (!histRes.data.messages || histRes.data.messages.length === 0) {
          fetchOpener();
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    if (user) fetchData();
  }, [week, user?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchOpener = async () => {
    setLoadingOpener(true);
    try {
      const { data } = await axios.post(`${API}/coach/open`, { week }, { withCredentials: true, timeout: 60000 });
      setOpeningQuestion(data.question);
    } catch (e) { console.error(e); }
    finally { setLoadingOpener(false); }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await axios.get(`${API}/progress/summary/${week}`, { withCredentials: true });
      if (data.status === 'ready') {
        setSummary(data);
      } else {
        // Poll for summary (generated async)
        setTimeout(fetchSummary, 5000);
      }
    } catch (e) { console.error(e); }
    finally { setSummaryLoading(false); }
  };

  const refreshCoachingStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/progress/coaching-status/${week}`, { withCredentials: true });
      setCoachingStatus(data);
    } catch (e) { console.error(e); }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, created_at: new Date().toISOString() }]);
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/coach/chat`, {
        message: userMsg,
        session_id: sessionId,
        week: week
      }, { withCredentials: true, timeout: 120000 });

      setMessages(prev => [...prev, { role: 'assistant', content: data.response, created_at: new Date().toISOString() }]);
      refreshCoachingStatus();
    } catch (e) {
      setMessages(prev => [...prev, { role: 'error', content: 'Connection issue. Please try again.', created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API}/progress/complete`, { week, reflection }, { withCredentials: true });
      setCompleted(true);
      await checkAuth();
      // Start polling for the summary (generated async on backend)
      setTimeout(fetchSummary, 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error completing module');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="p-12 text-center">
        <p className="text-[#737373]">Module not found</p>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="p-12 max-w-2xl mx-auto text-center">
        <Lock className="w-12 h-12 text-[#737373] mx-auto mb-4" />
        <h2 className="text-white font-bold text-xl mb-2">Module Locked</h2>
        <p className="text-[#A3A3A3] mb-6">Complete Week {week - 1} first to unlock this module.</p>
        <button onClick={() => navigate('/curriculum')} className="text-[#FF3B30] hover:underline text-sm font-medium">
          &larr; Back to Curriculum
        </button>
      </div>
    );
  }

  const phaseColors = { 1: 'text-blue-400', 2: 'text-amber-400', 3: 'text-emerald-400', 4: 'text-purple-400' };

  return (
    <div data-testid="module-page" className="p-6 md:p-8 lg:p-10 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/curriculum')} className="text-[#A3A3A3] hover:text-white transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-xs tracking-[0.2em] uppercase font-bold ${phaseColors[module.phase] || 'text-[#FF3B30]'}`}>
              Phase {module.phase}: {module.phase_name}
            </span>
            {(completed || isComplete) && (
              <span className="text-xs font-bold bg-[#22C55E]/10 text-[#22C55E] px-2 py-0.5 rounded-sm">Completed</span>
            )}
          </div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-white mt-1">
            Week {module.week}: {module.title}
          </h1>
        </div>
      </div>

      {/* The Shift */}
      {module.the_shift && (
        <div data-testid="module-the-shift" className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-sm px-6 py-4 mb-6">
          <p className="text-[#FF3B30] text-xs font-bold tracking-[0.15em] uppercase mb-1">The Shift This Week</p>
          <p className="text-white text-sm leading-relaxed italic">{module.the_shift}</p>
        </div>
      )}

      {/* Module Content */}
      <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-6">
        <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">{module.description}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-[#FF3B30]" />
              <span className="text-white text-sm font-semibold">Objectives</span>
            </div>
            <ul className="space-y-2">
              {module.objectives.map((obj, i) => (
                <li key={i} className="text-[#A3A3A3] text-sm flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] shrink-0 mt-1.5" />
                  {obj}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Dumbbell className="w-4 h-4 text-[#FF3B30]" />
              <span className="text-white text-sm font-semibold">This Week&apos;s Exercise</span>
            </div>
            <p className="text-[#A3A3A3] text-sm bg-[#0A0A0A] border border-[#262626] rounded-sm p-4 leading-relaxed">
              {module.exercise}
            </p>
          </div>
        </div>
      </div>

      {/* Enriched Content (Phase 1+) */}
      {(module.key_concepts || module.assigned_reading || module.case_study || module.discussion_questions || module.tools_frameworks) && (
        <div className="space-y-4 mb-6">
          {/* Key Concepts */}
          {module.key_concepts?.length > 0 && (
            <div data-testid="module-key-concepts" className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-white text-sm font-semibold">Key Concepts</span>
              </div>
              <ul className="space-y-3">
                {module.key_concepts.map((c, i) => (
                  <li key={i} className="text-[#A3A3A3] text-sm leading-relaxed flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0 mt-1.5" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Case Study */}
          {module.case_study && (
            <div data-testid="module-case-study" className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <Quote className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-white text-sm font-semibold">Case Study</span>
              </div>
              <div className="text-[#A3A3A3] text-sm leading-relaxed bg-[#0A0A0A] border border-[#262626] rounded-sm p-4 whitespace-pre-line">
                {module.case_study}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Discussion Questions */}
            {module.discussion_questions?.length > 0 && (
              <div data-testid="module-discussion-questions" className="bg-[#141414] border border-[#262626] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-4 h-4 text-[#FF3B30]" />
                  <span className="text-white text-sm font-semibold">Reflect On</span>
                </div>
                <ul className="space-y-3">
                  {module.discussion_questions.map((q, i) => (
                    <li key={i} className="text-[#A3A3A3] text-sm leading-relaxed flex items-start gap-2.5">
                      <span className="text-[#FF3B30] font-bold shrink-0">{i + 1}.</span>
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tools & Frameworks */}
            {module.tools_frameworks?.length > 0 && (
              <div data-testid="module-tools" className="bg-[#141414] border border-[#262626] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Wrench className="w-4 h-4 text-[#22C55E]" />
                  <span className="text-white text-sm font-semibold">Tools & Frameworks</span>
                </div>
                <ul className="space-y-2">
                  {module.tools_frameworks.map((t, i) => (
                    <li key={i} className="text-[#A3A3A3] text-sm leading-relaxed flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] shrink-0 mt-1.5" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Assigned Reading */}
          {module.assigned_reading?.length > 0 && (
            <div data-testid="module-reading" className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-[#A78BFA]" />
                <span className="text-white text-sm font-semibold">Assigned Reading</span>
              </div>
              <ul className="space-y-2">
                {module.assigned_reading.map((r, i) => (
                  <li key={i} className="text-[#A3A3A3] text-sm leading-relaxed flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] shrink-0 mt-1.5" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Coaching Session — The Core Experience */}
      <div className="bg-[#141414] border border-[#262626] rounded-sm mb-6 overflow-hidden">
        <div className="border-b border-[#262626] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold">Coaching Session — Week {module.week}</p>
              <p className="text-[#737373] text-xs">{module.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {coachingStatus.coaching_complete ? (
              <span data-testid="coaching-complete-badge" className="text-xs font-bold bg-[#22C55E]/10 text-[#22C55E] px-2 py-1 rounded-sm flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Coaching Done
              </span>
            ) : (
              <span data-testid="coaching-progress-badge" className="text-xs font-bold bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-1 rounded-sm">
                {coachingStatus.user_messages}/2 exchanges
              </span>
            )}
          </div>
        </div>

        {/* Messages area */}
        <div className="h-80 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && !loadingOpener && openingQuestion && (
            <div data-testid="module-opening-question" className="flex gap-3">
              <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
              </div>
              <div className="max-w-[80%] bg-[#0A0A0A] border border-[#FF3B30]/20 rounded-sm p-4 text-sm text-[#E5E5E5] leading-relaxed">
                {openingQuestion}
              </div>
            </div>
          )}
          {messages.length === 0 && loadingOpener && (
            <div className="flex gap-3">
              <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-[#FF3B30] animate-spin" />
                <span className="text-[#737373] text-sm">Your coach is preparing a question for this week...</span>
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            msg.role === 'error' ? (
              <div key={i} className="flex gap-3">
                <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/30 rounded-sm p-3 text-[#F59E0B] text-sm">{msg.content}</div>
              </div>
            ) : (
              <div key={i} data-testid={`module-chat-${i}`} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                    <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-sm p-3.5 text-sm leading-relaxed
                  ${msg.role === 'user' ? 'bg-[#FF3B30] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#E5E5E5]'}`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-invert max-w-none [&_p]:mb-2 [&_ul]:mb-2 [&_li]:text-[#E5E5E5] [&_strong]:text-white">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 bg-[#262626] rounded-full flex items-center justify-center shrink-0 mt-1">
                    <User className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  </div>
                )}
              </div>
            )
          ))}
          {sending && (
            <div className="flex gap-3">
              <div className="w-7 h-7 bg-[#FF3B30]/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3.5 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-[#FF3B30] animate-spin" />
                <span className="text-[#737373] text-sm">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {!completed && !isComplete && (
          <div className="border-t border-[#262626] p-3 flex gap-2">
            <textarea
              ref={inputRef}
              data-testid="module-coach-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Respond to your coach..."
              rows={1}
              className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm resize-none"
            />
            <button
              data-testid="module-coach-send"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="bg-[#FF3B30] hover:bg-[#D62828] text-white p-2.5 rounded-sm transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Completion Section */}
      {!completed && !isComplete && (
        <div className={`bg-[#141414] border rounded-sm p-6 transition-all ${coachingStatus.coaching_complete ? 'border-[#22C55E]/30' : 'border-[#262626] opacity-60'}`}>
          {!coachingStatus.coaching_complete ? (
            <div className="flex items-center gap-3 text-[#737373]">
              <MessageSquare className="w-5 h-5" />
              <p className="text-sm">Complete at least 2 coaching exchanges above to unlock module completion.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-white text-sm font-semibold">Reflection (optional)</span>
              </div>
              <textarea
                data-testid="module-reflection"
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                placeholder="What did you learn this week? What will you apply?"
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm resize-none h-24"
              />
              <button
                data-testid="complete-module-button"
                onClick={handleComplete}
                disabled={completing}
                className="bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold px-6 py-3 rounded-sm transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                {completing ? 'Completing...' : 'Complete Week ' + module.week}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Completed state */}
      {(completed || isComplete) && (
        <>
          {/* Weekly Summary */}
          {summaryLoading && !summary && (
            <div data-testid="summary-loading" className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-6">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-[#FF3B30] animate-spin" />
                <p className="text-[#A3A3A3] text-sm">Generating your personalized weekly summary...</p>
              </div>
            </div>
          )}
          {summary && (
            <div data-testid="weekly-summary" className="bg-[#141414] border border-[#FF3B30]/20 rounded-sm mb-6 overflow-hidden">
              <div className="border-b border-[#262626] px-5 py-3 flex items-center gap-2 bg-[#FF3B30]/5">
                <Sparkles className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-white text-sm font-semibold">Your Week {week} Summary</span>
              </div>
              <div className="p-5 space-y-5">
                {/* Growth Edge */}
                {summary.growth_edge && (
                  <div data-testid="summary-growth-edge" className="bg-[#0A0A0A] border border-[#FF3B30]/20 rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-[#FF3B30]" />
                      <span className="text-[#FF3B30] text-xs font-bold tracking-[0.15em] uppercase">Breakthrough Moment</span>
                    </div>
                    <p className="text-white text-sm leading-relaxed">{summary.growth_edge}</p>
                  </div>
                )}

                {/* Key Insights */}
                {summary.key_insights?.length > 0 && (
                  <div data-testid="summary-insights">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
                      <span className="text-white text-sm font-semibold">Key Insights</span>
                    </div>
                    <ul className="space-y-2">
                      {summary.key_insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0 mt-1.5" />
                          <span className="text-[#A3A3A3] leading-relaxed">{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Commitments */}
                {summary.commitments?.length > 0 && (
                  <div data-testid="summary-commitments">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-4 h-4 text-[#22C55E]" />
                      <span className="text-white text-sm font-semibold">Your Commitments</span>
                    </div>
                    <ul className="space-y-2">
                      {summary.commitments.map((c, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] shrink-0 mt-1.5" />
                          <span className="text-[#A3A3A3] leading-relaxed">{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Coach Observation + Assessment Tie-in */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {summary.coach_observation && (
                    <div data-testid="summary-observation" className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="w-3.5 h-3.5 text-[#FF3B30]" />
                        <span className="text-[#737373] text-xs font-bold tracking-[0.1em] uppercase">Coach&apos;s Note</span>
                      </div>
                      <p className="text-[#A3A3A3] text-sm leading-relaxed italic">{summary.coach_observation}</p>
                    </div>
                  )}
                  <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-3.5 h-3.5 text-[#F59E0B]" />
                      <span className="text-[#737373] text-xs font-bold tracking-[0.1em] uppercase">Assessment Connection</span>
                    </div>
                    <p className="text-[#A3A3A3] text-xs leading-relaxed">
                      {summary.strength_applied && summary.strength_applied !== "None identified" && (
                        <span className="block mb-1"><span className="text-[#22C55E]">Strength used:</span> {summary.strength_applied}</span>
                      )}
                      {summary.growth_area_addressed && summary.growth_area_addressed !== "None identified" && (
                        <span className="block"><span className="text-[#FF3B30]">Growth area:</span> {summary.growth_area_addressed}</span>
                      )}
                      {(!summary.strength_applied || summary.strength_applied === "None identified") &&
                       (!summary.growth_area_addressed || summary.growth_area_addressed === "None identified") && (
                        <span className="text-[#525252]">Continue engaging with coaching to build stronger connections to your assessment.</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Completion Bar */}
          <div className="bg-[#141414] border border-[#22C55E]/30 rounded-sm p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#22C55E]" />
              <div>
                <p className="text-white font-semibold">Week {module.week} Complete</p>
                <p className="text-[#737373] text-xs">Great work. Keep building on this momentum.</p>
              </div>
            </div>
            {week < 24 && (
              <button
                data-testid="next-module-button"
                onClick={() => navigate(`/module/${week + 1}`)}
                className="flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold px-5 py-2.5 rounded-sm transition-colors text-sm"
              >
                Week {week + 1} <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
